import React, { useEffect, useMemo, useState, Component } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DatabaseIcon, LinkIcon, PlusIcon, XIcon, BarChart3Icon, LineChartIcon, PieChartIcon, TableIcon, BookmarkIcon, WandSparklesIcon } from 'lucide-react';
import { datasets, getDataset } from '../data/datasets';
import { joinsForDataset, getJoin, getMergedFields } from '../data/joins';
import { ReportConfig, ReportFilter, ChartType, AggregateFn } from '../data/reports';
import { runReport } from '../utils/reportEngine';
import { ReportTable } from '../components/reports/ReportTable';
import { ReportChart } from '../components/reports/ReportChart';
import { ExportButton } from '../components/reports/ExportButton';
import { SaveTemplateDialog } from '../components/builder/SaveTemplateDialog';
import { BuilderStartWizard } from '../components/builder/BuilderStartWizard';
import { useApp } from '../context/AppContext';
import { useScreenInit } from '../useScreenInit.js';
const chartOptions: {
  value: ChartType;
  label: string;
  icon: ComponentType<{
    className?: string;
  }>;
}[] = [{
  value: 'table',
  label: 'Table',
  icon: TableIcon
}, {
  value: 'bar',
  label: 'Bar',
  icon: BarChart3Icon
}, {
  value: 'line',
  label: 'Line',
  icon: LineChartIcon
}, {
  value: 'pie',
  label: 'Pie',
  icon: PieChartIcon
}];
function defaultConfig(datasetId: string): ReportConfig {
  const ds = getDataset(datasetId)!;
  return {
    datasetId,
    joinId: null,
    columns: ds.fields.slice(0, 5).map(f => f.key),
    filters: [],
    groupBy: null,
    metricField: null,
    metricFn: 'count',
    chartType: 'table'
  };
}
const labelCls = 'block text-[11px] font-semibold uppercase tracking-wider text-navy-400';
const selectCls = 'mt-1.5 h-9 w-full rounded-lg border border-navy-100 bg-white px-2.5 text-sm text-navy-900 focus:border-navy-300 focus:outline-none focus:ring-2 focus:ring-navy-900/10';
export function ReportBuilder() {
  const {
    templates,
    saveTemplate,
    showBuilderGuide
  } = useApp();
  const [searchParams, setSearchParams] = useSearchParams();
  const screenInit = useScreenInit() as {
    wizardOpen?: boolean;
  };
  const templateId = searchParams.get('template');
  const loadedTemplate = templates.find(t => t.id === templateId);
  const [config, setConfig] = useState<ReportConfig>(() => loadedTemplate?.config ?? defaultConfig('appointments'));
  const [saveOpen, setSaveOpen] = useState(false);
  // Offer the guided start whenever the builder is opened fresh (no template),
  // unless the user has hidden the guide (changeable in Settings).
  const [wizardOpen, setWizardOpen] = useState(() => screenInit.wizardOpen ?? (!loadedTemplate && showBuilderGuide));
  useEffect(() => {
    if (loadedTemplate) {
      setConfig(loadedTemplate.config);
      setWizardOpen(false);
    }
  }, [templateId]); // eslint-disable-line react-hooks/exhaustive-deps
  const dataset = getDataset(config.datasetId)!;
  const availableJoins = joinsForDataset(config.datasetId);
  const activeJoin = config.joinId ? getJoin(config.joinId) : undefined;
  const fields = useMemo(() => getMergedFields(config.datasetId, config.joinId), [config.datasetId, config.joinId]);
  const numericFields = fields.filter(f => f.type === 'number');
  const result = useMemo(() => runReport(config), [config]);
  const update = (patch: Partial<ReportConfig>) => setConfig(prev => ({
    ...prev,
    ...patch
  }));
  const handleDatasetChange = (id: string) => {
    setConfig(defaultConfig(id));
    if (templateId) setSearchParams({});
  };
  const handleJoinChange = (joinId: string) => {
    const prefix = activeJoin ? `${activeJoin.joinedId}.` : null;
    setConfig(prev => ({
      ...prev,
      joinId: joinId || null,
      // Drop selections that referenced the previous join's fields
      columns: prefix ? prev.columns.filter(c => !c.startsWith(prefix)) : prev.columns,
      filters: prefix ? prev.filters.filter(f => !f.field.startsWith(prefix)) : prev.filters,
      groupBy: prefix && prev.groupBy?.startsWith(prefix) ? null : prev.groupBy,
      metricField: prefix && prev.metricField?.startsWith(prefix) ? null : prev.metricField
    }));
  };
  const toggleColumn = (key: string) => {
    update({
      columns: config.columns.includes(key) ? config.columns.filter(c => c !== key) : [...config.columns, key]
    });
  };
  const addFilter = () => update({
    filters: [...config.filters, {
      field: fields[0].key,
      operator: 'contains',
      value: ''
    }]
  });
  const updateFilter = (index: number, patch: Partial<ReportFilter>) => update({
    filters: config.filters.map((f, i) => i === index ? {
      ...f,
      ...patch
    } : f)
  });
  const removeFilter = (index: number) => update({
    filters: config.filters.filter((_, i) => i !== index)
  });
  const reportName = loadedTemplate?.name ?? 'Custom Report';
  const showChart = config.chartType !== 'table' && result?.grouped;
  return <div className="flex h-full flex-col gap-4 p-4 sm:p-5 lg:flex-row" data-id="element-344">
      {/* Config panel */}
      <div className="card-float w-full shrink-0 overflow-y-auto rounded-2xl bg-white p-5 thin-scroll lg:h-full lg:w-80" data-id="element-345">
        <div className="mb-5" data-id="element-346">
          <h2 className="text-sm font-semibold text-navy-900" data-id="element-347">
            {loadedTemplate ? `Editing: ${loadedTemplate.name}` : 'New Custom Report'}
          </h2>
          <p className="mt-0.5 text-xs text-navy-400" data-id="element-348">
            Configure your report — the preview updates live.
          </p>
          {!loadedTemplate && <button type="button" onClick={() => setWizardOpen(true)} className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-navy-50 px-3 py-1.5 text-xs font-medium text-navy-700 transition-colors hover:bg-navy-900 hover:text-gold-300" data-id="element-349">
              <WandSparklesIcon className="h-3.5 w-3.5" data-id="element-350" /> Build it with me
            </button>}
        </div>

        <div className="space-y-6" data-id="element-351">
          <div data-id="element-352">
            <label htmlFor="dataset" className={labelCls} data-id="element-353">
              <span className="inline-flex items-center gap-1.5" data-id="element-354">
                <DatabaseIcon className="h-3 w-3" data-id="element-355" /> Dataset
              </span>
            </label>
            <select id="dataset" value={config.datasetId} onChange={e => handleDatasetChange(e.target.value)} className={selectCls} data-id="element-356">
              {datasets.map(d => <option key={d.id} value={d.id} data-id="element-357">
                  {d.name}
                </option>)}
            </select>
            <p className="mt-1.5 text-[11px] leading-relaxed text-navy-300" data-id="element-358">
              {dataset.description}
            </p>
          </div>

          {availableJoins.length > 0 && <div data-id="element-359">
              <label htmlFor="join" className={labelCls} data-id="element-360">
                <span className="inline-flex items-center gap-1.5" data-id="element-361">
                  <LinkIcon className="h-3 w-3" data-id="element-362" /> Join Dataset
                </span>
              </label>
              <select id="join" value={config.joinId ?? ''} onChange={e => handleJoinChange(e.target.value)} className={selectCls} data-id="element-363">
                <option value="" data-id="element-364">None (single dataset)</option>
                {availableJoins.map(j => <option key={j.id} value={j.id} data-id="element-365">
                    + {j.name}
                  </option>)}
              </select>
              {activeJoin ? <p className="mt-1.5 text-[11px] leading-relaxed text-navy-300" data-id="element-366">
                  {activeJoin.description} Rows without a match keep blank
                  joined fields; multiple matches repeat the base row.
                </p> : <p className="mt-1.5 text-[11px] leading-relaxed text-navy-300" data-id="element-367">
                  Combine this dataset with a related one using a curated
                  relationship.
                </p>}
            </div>}

          <fieldset data-id="element-368">
            <legend className={labelCls} data-id="element-369">Columns</legend>
            <div className="mt-2 space-y-0.5" data-id="element-370">
              {fields.map(f => <label key={f.key} className="flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm text-navy-800 transition-colors hover:bg-navy-50" data-id="element-371">
                  <input type="checkbox" checked={config.columns.includes(f.key)} onChange={() => toggleColumn(f.key)} className="h-4 w-4 rounded border-navy-200 accent-[#102a43] focus:ring-navy-300" data-id="element-372" />
                  <span className="flex-1" data-id="element-373">{f.label}</span>
                  <span className="text-[10px] uppercase text-navy-300" data-id="element-374">
                    {f.type}
                  </span>
                </label>)}
            </div>
          </fieldset>

          <div data-id="element-375">
            <div className="flex items-center justify-between" data-id="element-376">
              <span className={labelCls} data-id="element-377">Filters</span>
              <button type="button" onClick={addFilter} className="inline-flex items-center gap-1 text-xs font-medium text-accent-700 transition-colors hover:text-accent-800" data-id="element-378">
                <PlusIcon className="h-3.5 w-3.5" data-id="element-379" /> Add
              </button>
            </div>
            {config.filters.length === 0 ? <p className="mt-2 text-xs text-navy-300" data-id="element-380">No filters applied.</p> : <div className="mt-2 space-y-2" data-id="element-381">
                {config.filters.map((f, i) => <div key={i} className="rounded-xl bg-navy-50/60 p-2.5" data-id="element-382">
                    <div className="flex items-center gap-2" data-id="element-383">
                      <select value={f.field} onChange={e => updateFilter(i, {
                  field: e.target.value
                })} aria-label="Filter field" className="h-8 flex-1 rounded-lg border border-navy-100 bg-white px-2 text-xs text-navy-900 focus:border-navy-300 focus:outline-none" data-id="element-384">
                        {fields.map(df => <option key={df.key} value={df.key} data-id="element-385">
                            {df.label}
                          </option>)}
                      </select>
                      <select value={f.operator} onChange={e => updateFilter(i, {
                  operator: e.target.value as ReportFilter['operator']
                })} aria-label="Filter operator" className="h-8 w-24 rounded-lg border border-navy-100 bg-white px-2 text-xs text-navy-900 focus:border-navy-300 focus:outline-none" data-id="element-386">
                        <option value="contains" data-id="element-387">contains</option>
                        <option value="equals" data-id="element-388">equals</option>
                      </select>
                      <button type="button" onClick={() => removeFilter(i)} aria-label="Remove filter" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-navy-300 transition-colors hover:bg-white hover:text-rose-600" data-id="element-389">
                        <XIcon className="h-3.5 w-3.5" data-id="element-390" />
                      </button>
                    </div>
                    <input type="text" value={f.value} onChange={e => updateFilter(i, {
                value: e.target.value
              })} placeholder="Value…" aria-label="Filter value" className="mt-2 h-8 w-full rounded-lg border border-navy-100 bg-white px-2 text-xs text-navy-900 placeholder:text-navy-300 focus:border-navy-300 focus:outline-none" data-id="element-391" />
                  </div>)}
              </div>}
          </div>

          <div data-id="element-392">
            <label htmlFor="groupBy" className={labelCls} data-id="element-393">
              Group By
            </label>
            <select id="groupBy" value={config.groupBy ?? ''} onChange={e => update({
            groupBy: e.target.value || null
          })} className={selectCls} data-id="element-394">
              <option value="" data-id="element-395">None (detail rows)</option>
              {fields.filter(f => f.type === 'string').map(f => <option key={f.key} value={f.key} data-id="element-396">
                    {f.label}
                  </option>)}
            </select>
          </div>

          {config.groupBy && <div className="grid grid-cols-2 gap-2" data-id="element-397">
              <div data-id="element-398">
                <label htmlFor="metricFn" className={labelCls} data-id="element-399">
                  Measure
                </label>
                <select id="metricFn" value={config.metricFn} onChange={e => {
              const fn = e.target.value as AggregateFn;
              update({
                metricFn: fn,
                metricField: fn === 'count' ? null : config.metricField ?? numericFields[0]?.key ?? null
              });
            }} className={selectCls} data-id="element-400">
                  <option value="count" data-id="element-401">Count</option>
                  <option value="sum" data-id="element-402">Sum</option>
                  <option value="avg" data-id="element-403">Average</option>
                </select>
              </div>
              <div data-id="element-404">
                <label htmlFor="metricField" className={labelCls} data-id="element-405">
                  Of Field
                </label>
                <select id="metricField" value={config.metricField ?? ''} onChange={e => update({
              metricField: e.target.value || null
            })} disabled={config.metricFn === 'count'} className={`${selectCls} disabled:bg-navy-50 disabled:text-navy-300`} data-id="element-406">
                  {config.metricFn === 'count' ? <option value="" data-id="element-407">Records</option> : numericFields.map(f => <option key={f.key} value={f.key} data-id="element-408">
                        {f.label}
                      </option>)}
                </select>
              </div>
            </div>}

          <fieldset data-id="element-409">
            <legend className={labelCls} data-id="element-410">Visualization</legend>
            <div className="mt-2 grid grid-cols-4 gap-1.5" role="radiogroup" aria-label="Visualization type" data-id="element-411">
              {chartOptions.map(opt => {
              const disabled = opt.value !== 'table' && !config.groupBy;
              return <button key={opt.value} type="button" role="radio" aria-checked={config.chartType === opt.value} disabled={disabled} title={disabled ? 'Set a Group By to enable charts' : opt.label} onClick={() => update({
                chartType: opt.value
              })} className={`flex flex-col items-center gap-1 rounded-xl border py-2.5 text-[11px] font-medium transition-all ${config.chartType === opt.value ? 'border-navy-900 bg-navy-900 text-white' : disabled ? 'cursor-not-allowed border-navy-50 text-navy-200' : 'border-navy-100 text-navy-500 hover:border-navy-300 hover:text-navy-900'}`} data-id="element-412">
                    <opt.icon className="h-4 w-4" data-id="element-413" />
                    {opt.label}
                  </button>;
            })}
            </div>
            {!config.groupBy && <p className="mt-1.5 text-[11px] text-navy-300" data-id="element-414">
                Charts require a Group By field.
              </p>}
          </fieldset>
        </div>
      </div>

      {/* Live preview */}
      <div className="flex min-w-0 flex-1 flex-col overflow-y-auto thin-scroll" data-id="element-415">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3" data-id="element-416">
          <div data-id="element-417">
            <div className="flex items-center gap-2" data-id="element-418">
              <h3 className="text-sm font-semibold text-navy-900" data-id="element-419">
                Live Preview
              </h3>
              {activeJoin && <span className="inline-flex items-center gap-1 rounded-full bg-accent-50 px-2 py-0.5 text-[10px] font-medium text-accent-800" data-id="element-420">
                  <LinkIcon className="h-2.5 w-2.5" data-id="element-421" />
                  {dataset.name} + {activeJoin.name}
                </span>}
            </div>
            <p className="text-xs text-navy-400" data-id="element-422">
              {result ? config.groupBy && result.grouped ? `${result.grouped.length} groups · ${result.rows.length} underlying records` : `${result.rows.length} records` : '—'}
            </p>
          </div>
          <div className="flex items-center gap-2" data-id="element-423">
            <button type="button" onClick={() => setSaveOpen(true)} className="card-float inline-flex h-9 items-center gap-2 rounded-full bg-white px-4 text-sm font-medium text-navy-800 transition-shadow hover:card-float-lg" data-id="element-424">
              <BookmarkIcon className="h-4 w-4 text-gold-500" data-id="element-425" />
              {loadedTemplate ? 'Update Template' : 'Save as Template'}
            </button>
            <ExportButton reportName={reportName} rowCount={result?.rows.length ?? 0} data-id="element-426" />
          </div>
        </div>

        <motion.div key={JSON.stringify(config)} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.18
      }} className="card-float overflow-hidden rounded-2xl bg-white" data-id="element-427">
          {result && showChart ? <div className="p-4" data-id="element-428">
              <ReportChart data={result.grouped!} chartType={config.chartType} metricLabel={result.metricLabel} data-id="element-429" />
            </div> : result && config.groupBy && result.grouped ? <ReportTable columns={[{
          key: 'group',
          label: fields.find(f => f.key === config.groupBy)?.label ?? 'Group'
        }, {
          key: 'value',
          label: result.metricLabel
        }]} rows={result.grouped.map(g => ({
          group: g.group,
          value: g.value
        }))} data-id="element-430" /> : result && config.columns.length > 0 ? <ReportTable columns={result.columns} rows={result.rows} data-id="element-431" /> : <div className="flex h-48 items-center justify-center text-sm text-navy-400" data-id="element-432">
              Select at least one column to preview data.
            </div>}
        </motion.div>
      </div>

      <BuilderStartWizard open={wizardOpen} onClose={() => setWizardOpen(false)} onStartBlank={() => {
      setConfig(defaultConfig('appointments'));
      setWizardOpen(false);
    }} onComplete={guidedConfig => {
      setConfig(guidedConfig);
      setWizardOpen(false);
      if (templateId) setSearchParams({});
    }} data-id="element-433" />

      <SaveTemplateDialog open={saveOpen} onClose={() => setSaveOpen(false)} config={config} existingTemplate={loadedTemplate} onSaved={id => {
      setSaveOpen(false);
      setSearchParams({
        template: id
      });
    }} saveTemplate={saveTemplate} data-id="element-434" />
    </div>;
}